import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Card, Group, Loader, SegmentedControl, Switch, Text } from '@mantine/core';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { GlassDatePicker } from '../components/shared/GlassDatePicker';
import { BinTablePanel } from '../components/side-panels/BinTablePanel';
import { CorrelationPanel } from '../components/side-panels/CorrelationPanel';
import { LineupPanel } from '../components/side-panels/LineupPanel';
import { ManeuversPanel } from '../components/side-panels/ManeuversPanel';
import { PerformanceBucketsPanel } from '../components/side-panels/PerformanceBucketsPanel';
import { PolarPanel } from '../components/side-panels/PolarPanel';
import { ScatterPanel } from '../components/side-panels/ScatterPanel';
import { useProcessingOverlay } from '../context/ProcessingOverlayContext';
import { EnhancedSelect } from '../EnhancedSelect';
import { ReactComponent as SailTrackLogo } from '../react-icons/SailTrackLogo.svg';
import { showMessage } from '../showMessage';

import type { HighlightSegment, Tack, TrackPoint } from '../types';

import '../styles/AnalyticsPage.css';

type ApiBoat = {
  id: number;
  name: string;
  type?: string | null;
  boat_type?: string | null;
  boat_id?: number | null;
  sharing?: {
    isOwner?: boolean;
    sharedBy?: string | null;
    sharedByName?: string | null;
    ownerName?: string | null;
  };
};
type ApiEvent = {
  id: number;
  event_name: string;
  event_date?: string | null;
  boat_id?: number | null;
  boat_name?: string | null;
  user_boat_id?: number | null;
  sharing?: {
    isOwner?: boolean;
    sharedBy?: string | null;
    sharedByName?: string | null;
    ownerName?: string | null;
  };
};
type ApiFile = {
  id: number;
  name?: string | null;
  display_name?: string | null;
  date?: string | null;
  event?: { id?: number; event_name?: string | null };
  sharing?: {
    isOwner?: boolean;
    sharedBy?: string | null;
    sharedByName?: string | null;
    ownerName?: string | null;
  };
};

type AnalyticsDataset = {
  track: TrackPoint[];
  tacks: Tack[];
  segmentKind: 'race' | 'training' | 'full';
  windKind: 'all' | 'upwind' | 'downwind';
  label: string;
};

type AnalyticsResponse = {
  success: boolean;
  datasets?: Array<{
    track: Array<Record<string, unknown>>;
    tacks: Tack[];
    segmentKind: AnalyticsDataset['segmentKind'];
    windKind: AnalyticsDataset['windKind'];
    label: string;
  }>;
  summary?: { samples?: number };
  message?: string;
};

type PanelMode =
  | 'polar'
  | 'correlation'
  | 'scatter'
  | 'bin'
  | 'maneuvers'
  | 'lineup'
  | 'performance';

const PANEL_OPTIONS: Array<{ value: PanelMode; labelKey: string; fallback: string }> = [
  { value: 'polar', labelKey: 'analytics.panels.polar', fallback: 'Polar' },
  { value: 'correlation', labelKey: 'analytics.panels.correlation', fallback: 'Correlation' },
  { value: 'scatter', labelKey: 'analytics.panels.scatter', fallback: 'Scatter' },
  { value: 'bin', labelKey: 'analytics.panels.bin', fallback: 'Bins' },
  { value: 'maneuvers', labelKey: 'analytics.panels.maneuvers', fallback: 'Maneuvers' },
  { value: 'lineup', labelKey: 'analytics.panels.lineup', fallback: 'Lineup' },
  { value: 'performance', labelKey: 'analytics.panels.performance', fallback: 'Performance' },
];
const DEFAULT_ANALYTICS_PANEL: PanelMode = 'polar';

const windOptions = [
  { value: 'all', label: 'All' },
  { value: 'upwind', label: 'Upwind' },
  { value: 'downwind', label: 'Downwind' },
] as const;

const segmentsDefault = { race: true, training: true, full: false };
const MAX_POINTS = 15_000;

const downsampleEvenSpacing = (track: TrackPoint[], maxPoints: number): TrackPoint[] => {
  if (track.length <= maxPoints) {
    return track;
  }
  const step = Math.max(1, Math.floor(track.length / maxPoints));
  const sampled: TrackPoint[] = [];
  for (let index = 0; index < track.length; index += step) {
    sampled.push(track[index]);
  }
  if (sampled.at(-1)?.globalIndex !== track.at(-1)?.globalIndex && track.at(-1)) {
    sampled.push(track.at(-1) as TrackPoint);
  }
  return sampled;
};

export const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { startProcess, endProcess } = useProcessingOverlay();
  const [boats, setBoats] = useState<ApiBoat[]>([]);
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [files, setFiles] = useState<ApiFile[]>([]);
  const [selectedBoats, setSelectedBoats] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [segments, setSegments] = useState(segmentsDefault);
  const [fromDate, setFromDate] = useState<string>('');
  const [wind, setWind] = useState<'all' | 'upwind' | 'downwind'>('all');
  const [panel, setPanel] = useState<PanelMode>(DEFAULT_ANALYTICS_PANEL);
  const [isLoading, setIsLoading] = useState(false);
  const [combinedTrack, setCombinedTrack] = useState<TrackPoint[]>([]);
  const [combinedTrackFull, setCombinedTrackFull] = useState<TrackPoint[]>([]);
  const [combinedTacks, setCombinedTacks] = useState<Tack[]>([]);
  const [activeTab, setActiveTab] = useState<'analysis' | 'settings'>('settings');
  const fromDateLabel = t('analytics.fromDate', 'From date');
  const ownerLabel = t('common.owner', 'Owner');
  const removeLabel = t('common.remove', 'Remove');

  const globalIndexMap = useMemo(() => {
    const map: Record<number, number> = {};
    for (const [idx, point] of combinedTrackFull.entries()) {
      if (typeof point.globalIndex === 'number') {
        map[point.globalIndex] = idx;
      } else {
        map[idx] = idx;
      }
    }
    return map;
  }, [combinedTrackFull]);

  const selectedBoatDetails = useMemo(
    () => boats.filter((boat) => selectedBoats.includes(String(boat.id))),
    [boats, selectedBoats],
  );

  const selectedEventDetails = useMemo(
    () => events.filter((event) => selectedEvents.includes(String(event.id))),
    [events, selectedEvents],
  );

  const selectedFileDetails = useMemo(
    () => files.filter((file) => selectedFiles.includes(String(file.id))),
    [files, selectedFiles],
  );

  const formatDateLabel = (value?: string | null) => {
    if (!value) {
      return t('reports.dateNotAvailable', 'Date not available');
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString();
  };

  const loadBoats = useCallback(async () => {
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!token || !apiBase) {
      return;
    }
    try {
      const response = await axios.post<{ success: boolean; boats?: ApiBoat[] }>(
        `${apiBase}/api/user/getBoats`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success && Array.isArray(response.data.boats)) {
        const normalizedBoats = response.data.boats.map((boat) => ({
          ...boat,
          type: boat.type ?? boat.boat_type ?? null,
        }));
        setBoats(normalizedBoats);
      }
    } catch (error) {
      console.error('Error loading boats', error);
    }
  }, []);

  const loadEventsForBoats = useCallback(
    async (boatIds: string[]) => {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      if (!token || !apiBase || boatIds.length === 0) {
        setEvents([]);
        return;
      }
      const accumulator: ApiEvent[] = [];
      for (const boatId of boatIds) {
        try {
          const response = await axios.post<{ success: boolean; events?: ApiEvent[] }>(
            `${apiBase}/api/user/getEvents`,
            { boat_id: Number.parseInt(boatId, 10) },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.data?.success && Array.isArray(response.data.events)) {
            const boatMeta = boats.find((boat) => String(boat.id) === boatId);
            const normalized = response.data.events.map((event) => ({
              ...event,
              boat_id: event.boat_id ?? event.user_boat_id ?? Number.parseInt(boatId, 10),
              user_boat_id: event.user_boat_id ?? event.boat_id ?? Number.parseInt(boatId, 10),
              boat_name: event.boat_name ?? boatMeta?.name ?? null,
            }));
            accumulator.push(...normalized);
          }
        } catch (error) {
          console.error('Error loading events', error);
        }
      }
      setEvents(accumulator);
    },
    [boats],
  );

  const loadFilesForEvents = useCallback(
    async (eventIds: string[]) => {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      if (!token || !apiBase || eventIds.length === 0) {
        setFiles([]);
        return;
      }
      const accumulator: ApiFile[] = [];
      for (const eventId of eventIds) {
        try {
          const response = await axios.post<{
            success: boolean;
            event?: { files?: ApiFile[] };
          }>(
            `${apiBase}/api/user/getEventsFiles`,
            { event_id: Number.parseInt(eventId, 10), filter: 'all' },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const files = response.data?.event?.files;
          if (Array.isArray(files)) {
            const eventMeta = events.find((ev) => String(ev.id) === eventId);
            accumulator.push(
              ...files.map((file) => {
                const fallbackEventName =
                  eventMeta?.event_name ?? (file as ApiFile).event?.event_name;
                return {
                  ...file,
                  event: file.event ?? {
                    id: eventMeta?.id,
                    event_name: fallbackEventName ?? undefined,
                  },
                };
              }),
            );
          }
        } catch (error) {
          console.error('Error loading files', error);
        }
      }
      setFiles(accumulator);
    },
    [events],
  );

  const fetchAnalytics = useCallback(async () => {
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!token || !apiBase) {
      const errorTitle = t('notifications.error', 'Error');
      const missingTokenMessage = t('errors.missingToken', 'Missing token');
      showMessage('error', errorTitle, missingTokenMessage);
      return;
    }
    const errorTitle = t('notifications.error', 'Error');
    const failedMessage = t('errors.failedLoadSettings', 'Failed to load data');

    const selection =
      selectedFiles.length > 0
        ? {
            boatIds: [] as number[],
            eventIds: [] as number[],
            fileIds: selectedFiles.map((id) => Number.parseInt(id, 10)),
          }
        : selectedEvents.length > 0
          ? {
              boatIds: [] as number[],
              eventIds: selectedEvents.map((id) => Number.parseInt(id, 10)),
              fileIds: [] as number[],
            }
          : {
              boatIds: selectedBoats.map((id) => Number.parseInt(id, 10)),
              eventIds: [] as number[],
              fileIds: [] as number[],
            };

    setIsLoading(true);
    startProcess();
    try {
      const response = await axios.post<AnalyticsResponse>(
        `${apiBase}/api/analytics/aggregate`,
        {
          boatIds: selection.boatIds,
          eventIds: selection.eventIds,
          fileIds: selection.fileIds,
          segments,
          fromDateUtc: fromDate || undefined,
          wind,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data?.success || !Array.isArray(response.data.datasets)) {
        const fallback = t('analytics.failedLoad', 'Failed to load analytics');
        showMessage('error', errorTitle, response.data?.message ?? fallback);
        setCombinedTrack([]);
        setCombinedTacks([]);
        return;
      }

      const mergedTrack: TrackPoint[] = [];
      const mergedTacks: Tack[] = [];
      for (const dataset of response.data.datasets) {
        const offset = mergedTrack.length;
        const trackPoints: TrackPoint[] = (dataset.track ?? []).map((row, index) => {
          const raw = row as unknown as TrackPoint & { globalIndex?: number };
          const sourceIndex =
            typeof raw.globalIndex === 'number' && Number.isFinite(raw.globalIndex)
              ? raw.globalIndex
              : index;
          return {
            ...raw,
            globalIndex: offset + sourceIndex,
          };
        });
        mergedTrack.push(...trackPoints);
        for (const tack of dataset.tacks ?? []) {
          mergedTacks.push({
            ...tack,
            start_index: tack.start_index + offset,
            end_index: tack.end_index + offset,
          });
        }
      }
      setCombinedTrackFull(mergedTrack);
      setCombinedTrack(downsampleEvenSpacing(mergedTrack, MAX_POINTS));
      setCombinedTacks(mergedTacks);
    } catch (error) {
      console.error('Error fetching analytics data', error);
      showMessage('error', errorTitle, failedMessage);
      setCombinedTrack([]);
      setCombinedTacks([]);
    } finally {
      setIsLoading(false);
      endProcess();
    }
  }, [
    selectedBoats,
    selectedEvents,
    selectedFiles,
    segments,
    fromDate,
    wind,
    t,
    startProcess,
    endProcess,
  ]);

  useEffect(() => {
    void loadBoats();
  }, [loadBoats]);

  useEffect(() => {
    void loadEventsForBoats(selectedBoats);
    setSelectedEvents([]);
    setFiles([]);
    setSelectedFiles([]);
  }, [selectedBoats, loadEventsForBoats]);

  useEffect(() => {
    void loadFilesForEvents(selectedEvents);
    setSelectedFiles([]);
  }, [selectedEvents, loadFilesForEvents]);

  const boatOptions = boats.map((boat) => {
    const sharedLabel =
      boat.sharing && boat.sharing.isOwner === false
        ? (boat.sharing.sharedBy ?? boat.sharing.sharedByName ?? boat.sharing.ownerName)
        : null;
    return {
      value: String(boat.id),
      label: sharedLabel ? `${boat.name} • ${sharedLabel}` : boat.name,
    };
  });
  const eventOptions = events.map((event) => {
    const dateLabel =
      event.event_date && !Number.isNaN(new Date(event.event_date).getTime())
        ? new Date(event.event_date).toLocaleDateString()
        : null;
    const label = dateLabel ? `${event.event_name} · ${dateLabel}` : event.event_name;
    return { value: String(event.id), label };
  });
  const fileOptions = files.map((file) => {
    const name = file.display_name || file.name || `File ${file.id}`;
    const date =
      file.date && !Number.isNaN(new Date(file.date).getTime())
        ? new Date(file.date).toLocaleDateString()
        : null;
    const label = date ? `${name} · ${date}` : name;
    return { value: String(file.id), label };
  });

  const selectedBoatEntries = boatOptions.filter((option) => selectedBoats.includes(option.value));
  const selectedEventEntries = eventOptions.filter((option) =>
    selectedEvents.includes(option.value),
  );
  const selectedFileEntries = fileOptions.filter((option) => selectedFiles.includes(option.value));
  const noneLabel = t('analytics.none', 'None');
  const totalSelectedLabel = t('analytics.totalSelected', 'Total selected');
  const totalSelections = selectedBoats.length + selectedEvents.length + selectedFiles.length;

  const renderPanel = () => {
    switch (panel) {
      case 'polar': {
        return <PolarPanel trackData={combinedTrack} />;
      }
      case 'correlation': {
        return <CorrelationPanel trackData={combinedTrack} />;
      }
      case 'scatter': {
        return <ScatterPanel trackData={combinedTrackFull} tacks={combinedTacks} />;
      }
      case 'bin': {
        return <BinTablePanel trackData={combinedTrack} />;
      }
      case 'maneuvers': {
        return (
          <ManeuversPanel
            trackData={combinedTrackFull}
            tacks={combinedTacks}
            globalToRelativeIndexMap={globalIndexMap}
          />
        );
      }
      case 'lineup': {
        return (
          <LineupPanel
            trackData={combinedTrackFull}
            tacks={combinedTacks}
            highlightSegments={[] as HighlightSegment[]}
            relatedTrackLayers={[]}
            globalToRelativeIndexMap={globalIndexMap}
          />
        );
      }
      case 'performance': {
        return <PerformanceBucketsPanel trackData={combinedTrack} />;
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="analytics-title">
          <Link
            to="/home"
            className="logo-link analytics-home-link"
            aria-label={t('common.home', 'Home')}
          >
            <SailTrackLogo className="logo-race" role="img" aria-hidden="true" focusable="false" />
          </Link>
          <div className="analytics-title-stack">
            <Text fw={800} fz={28} className="analytics-title-text" component="h1">
              {t('analytics.title', 'Aggregated data')}
            </Text>
            <Text size="sm" c="dimmed" className="analytics-subtitle-text">
              {t(
                'analytics.subtitle',
                'Review segments, wind, and shared files before exploring aggregated data.',
              )}
            </Text>
          </div>
        </div>

        <div className="analytics-tabs">
          <SegmentedControl
            value={activeTab}
            onChange={(value) => setActiveTab(value as 'analysis' | 'settings')}
            data={[
              { value: 'settings', label: t('analytics.tab.settings', 'Settings') },
              { value: 'analysis', label: t('analytics.tab.analysis', 'Analysis') },
            ]}
            aria-label={t('analytics.tabSwitcher', 'Select view')}
            size="md"
            radius="md"
          />
          <Button
            variant="filled"
            size="md"
            radius="md"
            onClick={() => void fetchAnalytics()}
            loading={isLoading}
            className="analytics-refresh-btn"
          >
            {t('analytics.refresh', 'Refresh data')}
          </Button>
        </div>
      </div>

      <div className="analytics-shell">
        {activeTab === 'settings' && (
          <>
            <Card
              shadow="sm"
              padding="md"
              radius="md"
              className="analytics-filters analytics-glass-card"
            >
              <div className="analytics-filters-head">
                <div>
                  <Text fw={700} fz="lg" className="analytics-section-title">
                    {t('analytics.filtersTitle', 'Data filters')}
                  </Text>
                  <Text size="sm" c="dimmed" className="analytics-section-subtitle">
                    {t(
                      'analytics.filtersHint',
                      'Pick the sources you want to blend together before refreshing the analysis.',
                    )}
                  </Text>
                </div>
                <div className="analytics-count-row" aria-live="polite"></div>
              </div>

              <div className="analytics-settings-grid">
                <div className="field">
                  <Text fw={600} className="field-label">
                    {t('race.placeholders.boat', 'Boat')}
                  </Text>
                  <EnhancedSelect
                    options={boatOptions}
                    value=""
                    placeholder={t('race.placeholders.boat', 'Boat')}
                    keepOpenOnSelect
                    selectedValues={new Set(selectedBoats)}
                    onChange={(val) =>
                      setSelectedBoats((prev) =>
                        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
                      )
                    }
                    valueLabel={`${selectedBoats.length}`}
                  />
                </div>
                <div className="field">
                  <Text fw={600} className="field-label">
                    {t('race.placeholders.event', 'Event')}
                  </Text>
                  <EnhancedSelect
                    options={eventOptions}
                    value=""
                    placeholder={t('race.placeholders.event', 'Event')}
                    keepOpenOnSelect
                    selectedValues={new Set(selectedEvents)}
                    onChange={(val) =>
                      setSelectedEvents((prev) =>
                        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
                      )
                    }
                    valueLabel={`${selectedEvents.length}`}
                  />
                </div>
                <div className="field">
                  <Text fw={600} className="field-label">
                    {t('common.file', 'File')}
                  </Text>
                  <EnhancedSelect
                    options={fileOptions}
                    value=""
                    placeholder={t('common.file', 'File')}
                    keepOpenOnSelect
                    selectedValues={new Set(selectedFiles)}
                    onChange={(val) =>
                      setSelectedFiles((prev) =>
                        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
                      )
                    }
                    valueLabel={`${selectedFiles.length}`}
                  />
                </div>
              </div>

              <div className="analytics-filters-row">
                <div className="analytics-filter-block">
                  <div className="analytics-inline-heading">
                    <Text fw={600} size="sm" className="field-inline-label">
                      {t('analytics.segments', 'Segments')}
                    </Text>
                    <Text size="xs" c="dimmed" className="field-inline-hint">
                      {t(
                        'analytics.segmentsHint',
                        'Choose which portions of each file to include.',
                      )}
                    </Text>
                  </div>
                  <div className="analytics-toggles-group">
                    <Switch
                      checked={segments.race}
                      onChange={(event) => {
                        const next = event?.currentTarget?.checked ?? !segments.race;
                        setSegments((prev) => ({
                          race: next,
                          training: prev.training,
                          full: next ? false : prev.full,
                        }));
                      }}
                      label={t('race.segmentEditor.race', 'Race / manga')}
                      size="md"
                    />
                    <Switch
                      checked={segments.training}
                      onChange={(event) => {
                        const next = event?.currentTarget?.checked ?? !segments.training;
                        setSegments((prev) => ({
                          race: prev.race,
                          training: next,
                          full: next ? false : prev.full,
                        }));
                      }}
                      label={t('race.segmentEditor.training', 'Training')}
                      size="md"
                    />
                    <Switch
                      checked={segments.full}
                      onChange={(event) => {
                        const next = event?.currentTarget?.checked ?? !segments.full;
                        setSegments((prev) => ({
                          race: next ? false : prev.race,
                          training: next ? false : prev.training,
                          full: next,
                        }));
                      }}
                      label={t('analytics.fullFile', 'Full file')}
                      size="md"
                    />
                  </div>
                </div>

                <div className="analytics-controls-group">
                  <div className="analytics-date-inline">
                    <span className="field-inline-label">{fromDateLabel}</span>
                    <GlassDatePicker
                      placeholder={t('analytics.selectDate', 'Select a date')}
                      value={fromDate}
                      onChange={(value) => setFromDate(value)}
                      className="analytics-date-picker"
                      ariaLabel={fromDateLabel}
                    />
                  </div>
                  <div className="analytics-date-inline">
                    <span className="field-inline-label">{t('analytics.windTitle', 'Wind')}</span>
                    <SegmentedControl
                      value={wind}
                      onChange={(value) => setWind(value as typeof wind)}
                      data={windOptions.map((option) => ({
                        value: option.value,
                        label: t(`analytics.wind.${option.value}`, option.label),
                      }))}
                      className="wind-selector"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <div className="analytics-selection-cards">
              <Card
                shadow="sm"
                padding="md"
                radius="md"
                className="selection-card analytics-glass-card"
              >
                <div className="selection-card__header">
                  <div>
                    <Text fw={700} className="selection-card-title">
                      {t('analytics.selectedBoats', 'Boats selected')}
                    </Text>
                    <Text size="sm" c="dimmed" className="selection-card-subtitle">
                      {t('analytics.boatsHint', 'Blend owner and shared boats for comparisons.')}
                    </Text>
                  </div>
                  <span className="selection-count">{selectedBoatEntries.length}</span>
                </div>

                <div className="selection-list">
                  {selectedBoatEntries.length === 0 ? (
                    <span className="chip-empty">{noneLabel}</span>
                  ) : (
                    selectedBoatDetails.map((boat) => (
                      <div key={boat.id} className="selection-item">
                        <div className="selection-info">
                          <div className="selection-title-row">
                            <span className="chip-label">{boat.name}</span>
                            {boat.sharing?.isOwner === false ? (
                              <span className="selection-tag">
                                {boat.sharing.sharedBy ??
                                  boat.sharing.sharedByName ??
                                  boat.sharing.ownerName}
                              </span>
                            ) : (
                              <span className="selection-tag selection-tag--owner">
                                {ownerLabel}
                              </span>
                            )}
                          </div>
                          <div className="selection-meta-row">
                            {boat.type ? (
                              <span className="selection-tag selection-tag--type">{boat.type}</span>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          aria-label={removeLabel}
                          onClick={() =>
                            setSelectedBoats((prev) => prev.filter((id) => id !== String(boat.id)))
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              <Card
                shadow="sm"
                padding="md"
                radius="md"
                className="selection-card analytics-glass-card"
              >
                <div className="selection-card__header">
                  <div>
                    <Text fw={700} className="selection-card-title">
                      {t('analytics.selectedEvents', 'Events selected')}
                    </Text>
                    <Text size="sm" c="dimmed" className="selection-card-subtitle">
                      {t('analytics.eventsHint', 'Pair regattas and training blocks by date.')}
                    </Text>
                  </div>
                  <span className="selection-count">{selectedEventEntries.length}</span>
                </div>

                <div className="selection-list">
                  {selectedEventEntries.length === 0 ? (
                    <span className="chip-empty">{noneLabel}</span>
                  ) : (
                    selectedEventDetails.map((event) => (
                      <div key={event.id} className="selection-item">
                        <div className="selection-info">
                          <div className="selection-title-row">
                            <span className="chip-label">{event.event_name}</span>
                            <span
                              className={
                                event.sharing?.isOwner === false
                                  ? 'selection-tag'
                                  : 'selection-tag selection-tag--owner'
                              }
                            >
                              {event.sharing?.isOwner === false
                                ? (event.sharing.sharedBy ??
                                  event.sharing.sharedByName ??
                                  event.sharing.ownerName ??
                                  t('settings.privacy.shareDataHint', 'Shared with me'))
                                : ownerLabel}
                            </span>
                          </div>
                          <div className="selection-meta-row">
                            {event.boat_name ? (
                              <span className="selection-tag selection-tag--boat">
                                {event.boat_name}
                              </span>
                            ) : null}
                            <span className="selection-meta">
                              {formatDateLabel(event.event_date)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          aria-label={removeLabel}
                          onClick={() =>
                            setSelectedEvents((prev) =>
                              prev.filter((id) => id !== String(event.id)),
                            )
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              <Card
                shadow="sm"
                padding="md"
                radius="md"
                className="selection-card analytics-glass-card"
              >
                <div className="selection-card__header">
                  <div>
                    <Text fw={700} className="selection-card-title">
                      {t('analytics.selectedFiles', 'Files selected')}
                    </Text>
                    <Text size="sm" c="dimmed" className="selection-card-subtitle">
                      {t('analytics.filesHint', 'Confirm the exact recordings tied to each event.')}
                    </Text>
                  </div>
                  <span className="selection-count">{selectedFileEntries.length}</span>
                </div>

                <div className="selection-list">
                  {selectedFileEntries.length === 0 ? (
                    <span className="chip-empty">{noneLabel}</span>
                  ) : (
                    selectedFileDetails.map((file) => (
                      <div key={file.id} className="selection-item">
                        <div className="selection-info">
                          <div className="selection-title-row">
                            <span className="chip-label">
                              {file.display_name ?? file.name ?? file.id}
                            </span>
                            <span
                              className={
                                file.sharing?.isOwner === false
                                  ? 'selection-tag'
                                  : 'selection-tag selection-tag--owner'
                              }
                            >
                              {file.sharing?.isOwner === false
                                ? (file.sharing.sharedBy ??
                                  file.sharing.sharedByName ??
                                  file.sharing.ownerName ??
                                  t('settings.privacy.shareDataHint', 'Shared with me'))
                                : ownerLabel}
                            </span>
                          </div>
                          <div className="selection-meta-row">
                            {file.event?.event_name ? (
                              <span className="selection-tag selection-tag--event">
                                {file.event.event_name}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          aria-label={removeLabel}
                          onClick={() =>
                            setSelectedFiles((prev) => prev.filter((id) => id !== String(file.id)))
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </>
        )}

        {activeTab === 'analysis' && (
          <div className="analytics-panel">
            <div className="analytics-subnav-container">
              <SegmentedControl
                value={panel}
                onChange={(value) => setPanel(value as PanelMode)}
                data={PANEL_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.labelKey, option.fallback),
                }))}
                fullWidth
                size="md"
                radius="md"
                className="analytics-main-tabs"
              />
              {isLoading ? (
                <Group gap={6} className="loading-indicator">
                  <Loader size="sm" type="dots" color="blue" />
                </Group>
              ) : null}
            </div>
            <Card
              shadow="sm"
              padding="md"
              radius="md"
              className="analytics-panel-card analytics-glass-card"
            >
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {!isLoading && combinedTrackFull.length === 0 ? (
                  <div className="analysis-empty">
                    <Text fw={700} className="analysis-empty__title">
                      {t('analytics.noDataTitle', 'No data yet')}
                    </Text>
                    <Text c="dimmed" ta="center" className="analysis-empty__subtitle">
                      {t(
                        'analytics.noData',
                        'Select boats, events or files in Settings and click “Refresh data”.',
                      )}
                    </Text>
                    <Button size="sm" onClick={() => void fetchAnalytics()} variant="light">
                      {t('analytics.refresh', 'Refresh data')}
                    </Button>
                  </div>
                ) : (
                  renderPanel()
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
